import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/_shared/lib/utils';

type InputProps = Omit<React.ComponentProps<'input'>, 'size'> & {
  /** Field height — `md` = 28px, `lg` = 32px */
  size?: 'md' | 'lg';
  /** With `type="password"`, renders a built-in show/hide toggle and manages masking internally.
   *  On by default for password fields; pass `revealable={false}` to opt out. */
  revealable?: boolean;
};

const SIZE_HEIGHT = { md: 'h-7', lg: 'h-8' } as const;

function Input({ className, type, size = 'md', revealable = true, id, ...props }: InputProps) {
  // Always have an id so the reveal toggle can point aria-controls at the field it controls.
  const reactId = React.useId();
  const inputId = id ?? reactId;
  const [revealed, setRevealed] = React.useState(false);
  const withToggle = revealable && type === 'password';

  const field = (
    <input
      id={inputId}
      type={withToggle && revealed ? 'text' : type}
      data-slot="input"
      data-size={size}
      className={cn(
        'w-full min-w-0 rounded-md border border-grey-75 bg-grey-0 px-2 py-1 text-base font-medium text-grey-800 transition-[color,box-shadow,border-color] outline-none md:text-sm',
        SIZE_HEIGHT[size],
        'selection:bg-primary selection:text-primary-foreground placeholder:font-normal placeholder:text-grey-300',
        // Number spinner: nudge the native up/down arrows off the digit — they're inline content, so
        // padding-right can't open the gap (it just shifts the whole box); a left margin on the spin button does.
        '[&::-webkit-inner-spin-button]:ml-1.5',
        'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        // Hover (unfocused): darker border + subtle grey glow, matching the secondary Button. Focus's brand wins once focused.
        'hover:border-grey-200 hover:shadow-secondary',
        // Focus (selected / typing): brand border + soft brand glow.
        'focus:border-primary focus:shadow-primary',
        // Error: red border + soft red glow; the red border holds through hover and focus.
        'aria-invalid:border-destructive aria-invalid:hover:border-destructive aria-invalid:hover:shadow-destructive aria-invalid:focus:border-destructive aria-invalid:focus:shadow-destructive',
        // Disabled: muted borderless tinted field — grey-25 fill, grey-400 text, no glow.
        'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-grey-25 disabled:text-grey-400 disabled:hover:border-transparent disabled:hover:shadow-none',
        // Read-only: muted fill/text; border stays muted (grey-75) on hover/focus.
        'read-only:bg-grey-25 read-only:text-grey-400 read-only:hover:border-grey-75 read-only:focus:border-grey-75 read-only:focus:shadow-secondary',
        // Leave room for the reveal toggle.
        withToggle && 'pr-8',
        // Without a wrapper the input is the root element, so it takes the caller's className.
        !withToggle && className,
      )}
      {...props}
    />
  );

  if (!withToggle) return field;

  return (
    // Wrapper is the root element here, so the caller's layout className belongs on it.
    <div className={cn('relative w-full', className)}>
      {field}
      <button
        type="button"
        onClick={() => setRevealed((v) => !v)}
        disabled={props.disabled}
        aria-controls={inputId}
        aria-pressed={revealed}
        aria-label={revealed ? 'Hide text' : 'Reveal text'}
        className="absolute top-1/2 right-2 flex size-4 -translate-y-1/2 items-center justify-center text-grey-500 transition-colors hover:text-grey-800 disabled:pointer-events-none disabled:text-grey-400"
      >
        {revealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

export { Input };
